// Growem — unified data model (client). Mirrors the Supabase schema; per-day set maps are
// plain objects (jsonb in the DB) so load/save is a 1:1 map.

export interface ExercisePair { n: string; t: string }

/** An alternative way to do the same movement (other implement/machine). Same muscle group; its name
 *  MUST be present in the plan's exercise-group map so logged sets keep their identity. */
export interface ExerciseAlt { n: string; t?: string; tip?: string }

export interface Exercise {
  n: string            // name
  meta: string         // prescription line, e.g. "3 × 8–12 · desc 2–3 min · RIR 1–2"
  last: string         // last mark shown as "últ:"
  rest: number         // ideal rest seconds (source of restIdeal; band derived from meta if not set)
  restMin?: number     // rest band lower bound (s) — resolver derives from meta when absent
  restIdeal?: number   // rest target (s) — defaults to `rest`
  restMax?: number     // rest band upper bound (s)
  sets: number         // set count
  t: string            // target string ('' when pair present)
  warn?: string        // safety warning
  tip?: string
  tempo?: string       // 4-number notation "1-2-4-3"
  tech?: string        // technique detail (overrides tip in display)
  pair?: [ExercisePair, ExercisePair]  // superset (Arm Day)
  alts?: ExerciseAlt[] // alternative implements for the same movement (variant picker; not for pairs)
}

export interface PlanDay { d: string; name: string; kick: string; ex: Exercise[] } // index 0=Lunes…5=Sábado

// rest = actual rest seconds (recorded by the timer); at = epoch ms when the set was completed
// (gives training a TIME dimension: session duration, pace, rest-per-exercise, time-of-day).
export interface SetLog { w?: number; r?: number; rir?: number; rest?: number; at?: number }

export type MealSource = 'ai_openai' | 'ai_local' | 'manual'

export interface Meal {
  id?: string
  day?: string
  t: string            // "HH:MM"
  n: string            // short name
  kcal: number
  prot: number
  carbs?: number
  fat?: number
  photo: 0 | 1
  photoPath?: string | null
  source?: MealSource
  note?: string
}

export interface LogEntry { id?: string; day?: string; t: string; icon: string; txt: string }
export interface ChatMsg { who: 'u' | 'g'; txt: string; seq?: number }

/** One calendar day's mutable state (day_state row + set maps). */
export interface DayState {
  day: string                        // local ISO "YYYY-MM-DD"
  planDayIndex: number | null
  done: Record<string, 1>            // set-key → 1
  notes: Record<string, string>
  weights: Record<string, string>    // "w{d}-{e}" → target override
  variants: Record<string, string>   // "d-e" → chosen variant name for THIS day (exercise swap)
  log: Record<string, SetLog>        // set-key → numeric inputs
  water: number                      // ml
  creatine: boolean
  prot: number                       // g
  kcal: number
  carbs: number
  fat: number
  sleep: string | null               // longest continuous stretch, e.g. "3h30"
  weight: number | null              // fasted bodyweight kg
  trainAt: string | null             // commitment time
}

export function defaultDay(day: string, planDayIndex: number | null = null): DayState {
  return {
    day, planDayIndex,
    done: {}, notes: {}, weights: {}, variants: {}, log: {},
    water: 0, creatine: false, prot: 0, kcal: 0, carbs: 0, fat: 0,
    sleep: null, weight: null, trainAt: null,
  }
}

export interface Profile {
  displayName: string
  avatarLetter: string
  bestStreak: number
  coachVoice: 'goggins' | 'sereno'
  cupSize: number          // ml
  timezone: string
  planStatus: PlanStatus   // routing gate: onboarding -> pending -> ready
  // Intake fields (collected at onboarding, persisted server-side). Surfaced client-side for the
  // Tier-2c starting-weight estimate (strengthStandards.ts). Null when never onboarded (Sebastian is
  // grandfathered) or in demo mode. weightKg is the onboarding SNAPSHOT; prefer the latest weigh-in.
  sex: Sexo | null
  bodyweightKg: number | null   // profiles.weight_kg (canonical kg), one-time onboarding snapshot
  heightCm: number | null       // profiles.height_cm
  dob: string | null            // profiles.dob (ISO 'YYYY-MM-DD'); age derived at read time
  level: Nivel | null           // profiles.level (principiante | intermedio | avanzado)
}

// ---- onboarding intake + plan gate ----
export type PlanStatus          = 'onboarding' | 'pending' | 'ready'
export type Sexo                = 'hombre' | 'mujer' | 'otro'
export type Objetivo            = 'hipertrofia' | 'perder_grasa' | 'recomposicion' | 'fuerza'
export type Nivel               = 'principiante' | 'intermedio' | 'avanzado'
export type Equipo              = 'gym_completo' | 'gym_basico' | 'casa_mancuernas' | 'peso_corporal'
export type Lesion              = 'hombro' | 'rodilla' | 'espalda_baja' | 'muneca' | 'codo' | 'cadera'
export type ObjetivoNutricional = 'volumen' | 'definicion' | 'mantenimiento'
export type Unidades            = 'kg' | 'lb'

/** Wire payload sent to submit_onboarding(p jsonb). Keys match the RPC's p->>'...' reads.
 *  estaturaCm is always cm; pesoKg / pesoMetaKg are always canonical kg (client converts lb->kg).
 *  v2 (2026-07-31): duracionMin / equipo / lesiones / objetivoNutricional are OPTIONAL - the form no
 *  longer asks them (system decides duration; "lugares" replaces equipo; nutrition derives from the
 *  main goal; injuries are one free-text nota). The RPC validates them only when present. */
export interface OnboardingPayload {
  nombre: string
  sexo: Sexo
  fechaNacimiento: string                 // ISO 'YYYY-MM-DD'
  estaturaCm: number
  pesoKg: number
  objetivo: Objetivo
  nivel: Nivel
  pesoMetaKg?: number
  dias: number[]                          // [0..6], 0=Lunes
  duracionMin?: number
  equipo?: Equipo
  lesiones?: Lesion[]
  lesionesNota?: string
  fuerza?: string
  objetivoNutricional?: ObjetivoNutricional
  restricciones?: string
  unidades: Unidades
}

export interface MuscleInfo { v: 'f' | 'b'; name: string; freq: string; days: string[]; note: string; ex: [string, string][] }
export interface StrengthSeries { g: string; ex: string; unit: string; vals: [string, number][] }
export interface FoodRule { re: RegExp; kcal: number; prot: number; carbs: number; fat: number; portion: string }

export interface MealEstimate {
  nombre: string
  kcal: number
  prot: number
  carbs: number
  fat: number
  note: string
  source: MealSource
}
